import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Search,
  MapPin,
  Briefcase,
  DollarSign,
  Clock,
  BookmarkPlus,
  Filter,
  X,
  ChevronLeft,
  ChevronRight,
  Eye,
  UserPlus,
  EyeOff,
  Loader2,
} from 'lucide-react'
import recruiterService, { Candidate, SearchFilters } from '@/services/recruiterService'
import { toast } from '@/stores/toastStore'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import SkillAutocomplete from '@/components/common/SkillAutocomplete'
import RangeSlider from '@/components/common/RangeSlider'
import AddToProspectsPanel from '@/components/recruiter/prospects/AddToProspectsPanel'
import QuotaBar from '@/components/recruiter/QuotaBar'
import type { SkillSearchResult } from '@/types'

export default function SearchCandidates() {
  const { t } = useTranslation('recruiter')
  const navigate = useNavigate()
  const [candidates, setCandidates] = useState<Candidate[]>([])
  const [loading, setLoading] = useState(false)
  const [showFilters, setShowFilters] = useState(true)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  })

  const [filters, setFilters] = useState<SearchFilters>({
    query: '',
    location: '',
    skills: [],
    yearsExperienceMin: undefined,
    yearsExperienceMax: undefined,
    salaryMin: undefined,
    salaryMax: undefined,
    employmentStatus: [],
    page: 1,
    limit: 20,
  })

  // Panel state
  const [addToProspectsCandidate, setAddToProspectsCandidate] = useState<{
    id: string
    name: string
  } | null>(null)
  const [isLoadingProfile, setIsLoadingProfile] = useState<string | null>(null)
  const [revealing, setRevealing] = useState<string | null>(null)
  const [searchParams] = useSearchParams()

  const hasActiveFilters =
    !!filters.query?.trim() ||
    !!(filters.skills && filters.skills.length > 0) ||
    !!filters.location?.trim() ||
    !!(filters.employmentStatus && filters.employmentStatus.length > 0) ||
    filters.yearsExperienceMin !== undefined ||
    filters.yearsExperienceMax !== undefined ||
    filters.salaryMin !== undefined ||
    filters.salaryMax !== undefined

  useEffect(() => {
    const queryFromUrl = searchParams.get('query')
    if (queryFromUrl) {
      setFilters((prev) => ({ ...prev, query: queryFromUrl }))
    }
    searchCandidates()
  }, [filters.page, searchParams])

  const searchCandidates = async () => {
    if (!hasActiveFilters) {
      setCandidates([])
      return
    }
    try {
      setLoading(true)
      // Warn if multiple employment statuses are selected (backend limitation)
      if (filters.employmentStatus && filters.employmentStatus.length > 1) {
        toast.info(
          `Note: Filtering by ${filters.employmentStatus[0]
            .replace(/_/g, ' ')
            .toLowerCase()
            .replace(/\b\w/g, (l) =>
              l.toUpperCase()
            )} only. Multiple status filtering is not yet supported.`
        )
      }
      const response = await recruiterService.searchCandidates(filters)
      setCandidates(response.candidates)
      setPagination(response.pagination)
    } catch (error: any) {
      console.error('Search error:', error)
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to search candidates'
      toast.error(typeof errorMessage === 'string' ? errorMessage : 'Failed to search candidates')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setFilters((prev) => ({ ...prev, page: 1 }))
    searchCandidates()
  }

  const handleFilterChange = (key: keyof SearchFilters, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }

  const addSkill = (skill: SkillSearchResult) => {
    if (!filters.skills?.includes(skill.name)) {
      setFilters((prev) => ({
        ...prev,
        skills: [...(prev.skills || []), skill.name],
      }))
    }
  }

  const removeSkill = (skillName: string) => {
    setFilters((prev) => ({
      ...prev,
      skills: prev.skills?.filter((s) => s !== skillName) || [],
    }))
  }

  const handleEmploymentStatusChange = (
    status: 'ACTIVELY_LOOKING' | 'OPEN_TO_OFFERS' | 'NOT_LOOKING'
  ) => {
    setFilters((prev) => {
      const currentStatuses = prev.employmentStatus || []
      const isChecked = currentStatuses.includes(status)

      return {
        ...prev,
        employmentStatus: isChecked
          ? currentStatuses.filter((s) => s !== status)
          : [...currentStatuses, status],
      }
    })
  }

  const clearFilters = () => {
    setFilters({
      query: '',
      location: '',
      skills: [],
      yearsExperienceMin: undefined,
      yearsExperienceMax: undefined,
      salaryMin: undefined,
      salaryMax: undefined,
      employmentStatus: [],
      page: 1,
      limit: 20,
    })
  }

  const handleSaveCandidate = async (candidateId: string) => {
    try {
      await recruiterService.saveCandidate(candidateId)
      toast.success('Candidate saved successfully')
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to save candidate')
    }
  }

  const handleViewProfile = async (candidateId: string) => {
    setIsLoadingProfile(candidateId)
    try {
      const fullCandidate = await recruiterService.viewCandidate(candidateId)
      let contactInfo = null
      if (fullCandidate.contactRevealed) {
        try {
          const { contact } = await recruiterService.revealContact(candidateId)
          contactInfo = contact
        } catch {
          // already revealed but could not re-fetch contact — navigate with just candidate
        }
      }
      navigate(`/recruiter/candidates/${candidateId}`, {
        state: { candidate: fullCandidate, contactInfo },
      })
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to load candidate profile')
    } finally {
      setIsLoadingProfile(null)
    }
  }

  const handleRevealCandidate = async (candidateId: string) => {
    setRevealing(candidateId)
    try {
      const fullCandidate = await recruiterService.viewCandidate(candidateId)
      let revealedData: Partial<Candidate> = {}
      if (fullCandidate.contactRevealed) {
        try {
          await recruiterService.revealContact(candidateId)
        } catch {
          // already revealed
        }
      }
      revealedData = {
        firstName: fullCandidate.firstName,
        lastName: fullCandidate.lastName,
        profilePhotoUrl: fullCandidate.profilePhotoUrl,
        contactRevealed: true,
      }
      setCandidates((prev) =>
        prev.map((c) => (c.id === candidateId ? { ...c, ...revealedData } : c))
      )
      toast.success('Contact revealed')
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to reveal contact')
    } finally {
      setRevealing(null)
    }
  }

  const getEmploymentStatusBadge = (status: string | null | undefined) => {
    if (!status) return null

    const colors = {
      ACTIVELY_LOOKING: 'bg-green-100 text-green-800',
      OPEN_TO_OFFERS: 'bg-blue-100 text-blue-800',
      NOT_LOOKING: 'bg-gray-100 text-gray-800',
    }

    const labels = {
      ACTIVELY_LOOKING: 'Actively Looking',
      OPEN_TO_OFFERS: 'Open to Offers',
      NOT_LOOKING: 'Not Looking',
    }

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status as keyof typeof colors]}`}
      >
        {labels[status as keyof typeof labels]}
      </span>
    )
  }

  return (
    <>
      <QuotaBar />
      <div className="container-custom py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Search Candidates</h1>
          <p className="text-gray-600">Find the perfect match for your team</p>
        </div>

        {/* Search Bar */}
        <Card className="mb-6">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="flex gap-3">
              <div className="flex-grow">
                <Input
                  type="text"
                  placeholder="Search by job title or keywords..."
                  value={filters.query}
                  onChange={(e) => handleFilterChange('query', e.target.value)}
                  icon={<Search className="w-5 h-5" />}
                />
              </div>
              <Button type="submit" variant="primary" isLoading={loading}>
                Search
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2"
              >
                <Filter className="w-4 h-4" />
                Filters
              </Button>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <div className="pt-4 border-t border-gray-200">
                <div className="grid md:grid-cols-2 gap-x-6 gap-y-3 mb-3">
                  {/* Left column */}
                  <div className="space-y-3">
                    <Input
                      type="text"
                      label="Location"
                      placeholder="e.g., Tunis"
                      value={filters.location}
                      onChange={(e) => handleFilterChange('location', e.target.value)}
                    />

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Employment Status
                      </label>
                      <div className="flex flex-wrap gap-1.5">
                        {[
                          { value: 'ACTIVELY_LOOKING' as const, label: 'Actively Looking' },
                          { value: 'OPEN_TO_OFFERS' as const, label: 'Open to Offers' },
                          { value: 'NOT_LOOKING' as const, label: 'Not Looking' },
                        ].map((opt) => {
                          const active = filters.employmentStatus?.includes(opt.value) || false
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => handleEmploymentStatusChange(opt.value)}
                              className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
                                active
                                  ? 'bg-blue-600 text-white border-blue-600'
                                  : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                              }`}
                            >
                              {opt.label}
                            </button>
                          )
                        })}
                      </div>
                    </div>

                    <div>
                      <SkillAutocomplete
                        label="Skills"
                        placeholder="Search skills…"
                        onSelect={addSkill}
                        selectedSkills={[]}
                      />
                      {filters.skills && filters.skills.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {filters.skills.map((skill) => (
                            <span
                              key={skill}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800"
                            >
                              {skill}
                              <button
                                type="button"
                                onClick={() => removeSkill(skill)}
                                className="hover:text-blue-900"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right column — sliders */}
                  <div className="space-y-3">
                    <RangeSlider
                      label="Experience (years)"
                      min={0}
                      max={30}
                      step={1}
                      value={[filters.yearsExperienceMin ?? 0, filters.yearsExperienceMax ?? 30]}
                      onChange={([min, max]) => {
                        setFilters((prev) => ({
                          ...prev,
                          yearsExperienceMin: min === 0 ? undefined : min,
                          yearsExperienceMax: max === 30 ? undefined : max,
                        }))
                      }}
                    />

                    <RangeSlider
                      label="Salary"
                      min={0}
                      max={20000}
                      step={100}
                      unit="TND"
                      value={[filters.salaryMin ?? 0, filters.salaryMax ?? 20000]}
                      onChange={([min, max]) => {
                        setFilters((prev) => ({
                          ...prev,
                          salaryMin: min === 0 ? undefined : min,
                          salaryMax: max === 20000 ? undefined : max,
                        }))
                      }}
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button type="button" variant="secondary" onClick={clearFilters} size="sm">
                    Clear All Filters
                  </Button>
                </div>
              </div>
            )}
          </form>
        </Card>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : candidates.length > 0 ? (
          <>
            <div className="mb-4 text-sm text-gray-600">
              Found {pagination.total} candidates (Page {pagination.page} of {pagination.totalPages}
              )
            </div>

            <div className="grid gap-6 mb-8">
              {candidates.map((candidate, index) => (
                <Card key={candidate.id} className="hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <img
                        src={
                          candidate.profilePhotoUrl ||
                          `https://cdn.jsdelivr.net/gh/alohe/avatars/png/bluey_${((candidate.id.charCodeAt(0) + candidate.id.charCodeAt(candidate.id.length - 1)) % 10) + 1}.png`
                        }
                        alt=""
                        className="w-16 h-16 rounded-full rounded-avatar object-cover"
                      />
                    </div>

                    <div className="flex-grow">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900">
                            {candidate.firstName || candidate.lastName
                              ? `${candidate.firstName ?? ''} ${candidate.lastName ?? ''}`.trim()
                              : `Candidate #${(pagination.page - 1) * pagination.limit + index + 1}`}
                          </h3>
                          {candidate.employmentStatus &&
                            getEmploymentStatusBadge(candidate.employmentStatus)}
                        </div>
                        <div className="flex gap-2 flex-wrap justify-end">
                          <Button
                            onClick={() => {
                              const name =
                                candidate.firstName || candidate.lastName
                                  ? `${candidate.firstName ?? ''} ${candidate.lastName ?? ''}`.trim()
                                  : `Candidate #${(pagination.page - 1) * pagination.limit + index + 1}`
                              setAddToProspectsCandidate({ id: candidate.id, name })
                            }}
                            variant="secondary"
                            size="sm"
                            className="flex items-center gap-2"
                          >
                            <UserPlus className="w-4 h-4" />
                            Add to Prospects
                          </Button>
                          <Button
                            onClick={() => handleSaveCandidate(candidate.id)}
                            variant="secondary"
                            size="sm"
                            className="flex items-center gap-2"
                          >
                            <BookmarkPlus className="w-4 h-4" />
                            Save
                          </Button>
                          {!candidate.contactRevealed && (
                            <Button
                              onClick={() => handleRevealCandidate(candidate.id)}
                              variant="secondary"
                              size="sm"
                              isLoading={revealing === candidate.id}
                              disabled={revealing !== null}
                              className="flex items-center gap-2"
                            >
                              {revealing === candidate.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <EyeOff className="w-4 h-4" />
                              )}
                              Reveal
                            </Button>
                          )}
                          <Button
                            onClick={() => handleViewProfile(candidate.id)}
                            variant="primary"
                            size="sm"
                            isLoading={isLoadingProfile === candidate.id}
                            disabled={isLoadingProfile !== null}
                            className="flex items-center gap-2"
                          >
                            <Eye className="w-4 h-4" />
                            {t('profileReveal.search.viewProfile')}
                          </Button>
                        </div>
                      </div>

                      {candidate.jobTitle && (
                        <div className="flex items-center gap-2 text-gray-700 mb-2">
                          <Briefcase className="w-4 h-4" />
                          <span className="font-medium">{candidate.jobTitle}</span>
                        </div>
                      )}

                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-3">
                        {candidate.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {candidate.location}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {candidate.yearsExperience} years experience
                        </span>
                        {candidate.salaryMin && candidate.salaryMax && (
                          <span className="flex items-center gap-1">
                            <DollarSign className="w-4 h-4" />
                            {candidate.salaryMin.toLocaleString()} -{' '}
                            {candidate.salaryMax.toLocaleString()} TND/month
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">
                          Profile {candidate.profileCompletion}% complete
                        </span>
                        <div className="flex-grow bg-gray-200 rounded-full h-2 max-w-xs">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all"
                            style={{ width: `${candidate.profileCompletion}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleFilterChange('page', pagination.page - 1)}
                  disabled={pagination.page === 1 || loading}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Button>

                <span className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.totalPages}
                </span>

                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleFilterChange('page', pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages || loading}
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </>
        ) : !hasActiveFilters ? (
          <Card className="text-center py-20">
            <Search className="w-16 h-16 text-blue-200 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {t('search.emptyState.title', 'Start your candidate search')}
            </h3>
            <p className="text-gray-500">
              {t(
                'search.emptyState.hint',
                'Enter keywords, a job title, or add skills above to find matching candidates.'
              )}
            </p>
          </Card>
        ) : (
          <Card className="text-center py-20">
            <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No candidates found</h3>
            <p className="text-gray-600 mb-6">Try adjusting your search criteria or filters</p>
            <Button onClick={clearFilters} variant="primary">
              Clear Filters
            </Button>
          </Card>
        )}

        {addToProspectsCandidate && (
          <AddToProspectsPanel
            isOpen={true}
            onClose={() => setAddToProspectsCandidate(null)}
            candidateId={addToProspectsCandidate.id}
            candidateName={addToProspectsCandidate.name}
            source="SEARCH"
          />
        )}
      </div>
    </>
  )
}
